import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { Price } from "@/lib/currency";
import { Input } from "@/components/ui/input";

export default function Reports() {
  const [q, setQ] = useState("");
  const [params] = useSearchParams();
  const [cat, setCat] = useState(params.get("category") || "");
  useEffect(() => { setCat(params.get("category") || ""); setPage(1); }, [params]);
  const [page, setPage] = useState(1);
  const { data: cats = [] } = useQuery({ queryKey: ["report-cats"], queryFn: async () => (await api.get("/reports/categories")).data });
  const { data } = useQuery({ queryKey: ["reports", q, cat, page], queryFn: async () => (await api.get("/reports", { params: { q, category: cat, page, size: 24 } })).data, keepPreviousData: true });
  const pages = Math.max(1, Math.ceil((data?.total || 0) / 24));
  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#03AB9E] mb-2">Special Reports</p>
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">Market Reports</h1>
      <p className="text-slate-400 mt-3 max-w-2xl">Market trends, forecast and opportunity analysis across {cats.length} chemical categories. Download free samples; purchased reports are available in your account.</p>
      <div className="grid lg:grid-cols-[260px_1fr] gap-8 mt-10">
        <aside className="space-y-1 lg:sticky lg:top-24 self-start max-h-[70vh] overflow-y-auto pr-2" data-testid="reports-category-list">
          {[{ name: "", count: cats.reduce((a, c) => a + c.count, 0) }, ...cats].map((c) => (
            <button key={c.name || "all"} onClick={() => { setCat(c.name); setPage(1); }} data-testid={`reports-cat-${c.name || "all"}`}
              className={`w-full flex justify-between text-left text-sm px-3 py-2 rounded-md ${cat === c.name ? "bg-[#1074BA]/20 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800/50"}`}>
              <span>{c.name || "All categories"}</span><span className="font-mono text-xs">{c.count}</span>
            </button>
          ))}
        </aside>
        <div>
          <div className="relative mb-6">
            <Search className="h-4 w-4 absolute left-3 top-3 text-slate-500" />
            <Input data-testid="reports-search-input" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search reports, e.g. acetic acid" className="pl-9 h-10 bg-slate-900 border-slate-700" />
          </div>
          <p className="text-xs font-mono text-slate-500 mb-4" data-testid="reports-total">{data?.total ?? "…"} reports</p>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {data?.items.map((r) => (
              <Link key={r.id} to={`/reports/${r.id}`} data-testid={`report-card-${r.id}`} className="flex flex-col bg-[#0F172A]/80 border border-slate-800 rounded-lg p-5 hover:border-[#1074BA] transition-colors">
                <p className="font-mono text-[10px] uppercase tracking-wider text-[#03AB9E]">{r.category}</p>
                <h3 className="font-medium mt-2 line-clamp-3 flex-1">{r.title}</h3>
                <div className="mt-4 flex items-center justify-between">
                  <p><Price value={r.price} /></p>
                  {r.has_sample && <span className="text-[10px] px-2 py-0.5 rounded border border-[#03AB9E]/50 text-[#03AB9E]">Sample</span>}
                </div>
              </Link>
            ))}
          </div>
          <div className="flex items-center justify-center gap-3 mt-8 font-mono text-sm">
            <button data-testid="reports-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-2 rounded hover:bg-slate-800 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
            <span>{page} / {pages}</span>
            <button data-testid="reports-next" disabled={page >= pages} onClick={() => setPage(page + 1)} className="p-2 rounded hover:bg-slate-800 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
